"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Card } from "@/components/ui/Card";
import PublicLayout from "@/components/layout/PublicLayout";
import Link from "next/link";
import { Eye, EyeOff } from "lucide-react";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function ReferralSignupForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    // Check for referral code from URL or localStorage persistence
    const initializeReferral = async () => {
      const urlCode = searchParams.get("ref");
      let codeToUse = urlCode;

      // If no URL code, try to get from localStorage persistence
      if (!codeToUse) {
        const { getReferralCode } = await import("@/lib/referral-persistence");
        codeToUse = getReferralCode();
      }

      // If we have a code, ensure it's stored in sessionStorage for recordReferralConversion
      if (codeToUse) {
        const { captureReferralCode } = await import("@/lib/referral-persistence");
        const { storeTrackingParams } = await import("@/lib/referral-tracking");
        
        captureReferralCode(codeToUse);
        storeTrackingParams({ referralCode: codeToUse });
        setReferralCode(codeToUse);
        console.log("Using referral code:", codeToUse);
      }
    };

    initializeReferral();

    // Load reCAPTCHA script only if configured
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
  window.grecaptcha.ready(() => {
    setRecaptchaReady(true);
  });
};
      document.head.appendChild(script);
    } else {
      // Skip reCAPTCHA in development
      setRecaptchaReady(true);
    }
  }, [searchParams]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.email.trim() || !form.password.trim() || !form.confirmPassword.trim()) {
      setError("Please complete all fields");
      return;
    }

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Logout any existing session
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      localStorage.removeItem("garage-data");
      localStorage.removeItem("user");

      // Get reCAPTCHA token (skip if not configured)
      let recaptchaToken = "";
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA") && window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "signup" });
      }

      // Create user account
      const response = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          recaptchaToken,
        }),
      });

      if (!response.ok) {
        try {
          const data = await response.json();
          setError(data.error || "Signup failed");
        } catch {
          setError(`Signup failed with status ${response.status}`);
        }
        return;
      }

      const data = await response.json();

      // Auto-login after successful signup
      const loginResponse = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          password: form.password,
          recaptchaToken: recaptchaToken || "",
        }),
      });

      if (loginResponse.ok) {
        const userData = await loginResponse.json();
        localStorage.setItem("user", JSON.stringify(userData.user));

        // Record referral conversion
        try {
          const { recordReferralConversion } = await import("@/lib/referral-tracking");
          await recordReferralConversion(form.email, form.name);
        } catch (err) {
          console.error("Failed to record referral conversion:", err);
        }

        // Always redirect to referral dashboard for referral signups
        router.push("/referrals?new=true&referred=true");
      } else {
        setError("Account created! Please login.");
        setTimeout(() => {
          router.push("/login");
        }, 2000);
      }
    } catch (error) {
      console.error("Signup error:", error);
      setError("An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="bg-linear-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-6 min-h-[70vh]">
        <div className="w-full max-w-md space-y-6">

          <Card className="p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Join Our Referral Program</h2>
            <p className="text-sm text-slate-600 mb-6">Start earning 20% recurring commission today</p>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSignup} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="John Doe"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute inset-y-0 right-0 px-3 text-slate-500 hover:text-slate-700"
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            <p className="text-center text-slate-600 text-sm mt-6">
              Already have an account?{" "}
              <Link href={`/referral-login${referralCode ? `?ref=${encodeURIComponent(referralCode)}` : ""}`} className="text-blue-600 font-semibold hover:underline">
                Login here
              </Link>
            </p>

            <p className="text-center text-xs text-slate-500 mt-4">
              This site is protected by reCAPTCHA and the Google{" "}
              <a href="https://policies.google.com/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="https://policies.google.com/terms" className="text-blue-600 hover:underline">
                Terms of Service
              </a>
            </p>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
}
